
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Alert, AlertProps, AlertTitle, Slide, Snackbar, Typography, styled } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/CancelOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import IHasChildren from '@/types/IHasChildren';
import AlertContext, { IAlert } from '@/context/AlertContext';


interface IAlertProviderProps extends IHasChildren { }

const StyledAlert = styled(Alert, { shouldForwardProp: (props) => props !== "styleSeverity" })<AlertProps & { styleSeverity?: "warning" | "info" | "success" | "error" }>(({ styleSeverity, theme }) => ({
    borderRadius: '10px',
    border: `1px solid ${theme.palette[styleSeverity ?? "info"].dark}55`,
    maxWidth: '550px',
}));

const StyledInfoIcon = styled(InfoOutlinedIcon)(() => ({

}));

const StyledWarningAmberIcon = styled(WarningAmberIcon)(() => ({

}))

const StyledCheckIcon = styled(CheckIcon)(() => ({

}));

const StyledCloseIcon = styled(CloseIcon)(() => ({

}));

const AlertProvider: FC<IAlertProviderProps> = ({
    children
}) => {
    const [alerts, setAlerts] = useState<IAlert[]>([]);
    const timerIds = useRef<number[]>([]);

    const removeAlert = useCallback((alert: IAlert) => {
        // Set alert to 'dismissed'
        setAlerts(c => {
            var index = c.indexOf(alert);
    
            if (index === -1) {
                return c;
            }

            const copy = [...c];

            copy[index].dismissed = true;

            return copy;
        });

        // in '1sec' remove the alert from the list
        const timer: { id?: number; } = {
            id: undefined
        };

        timer.id = window.setTimeout(() => {
            setAlerts(c => {
                var index = c.indexOf(alert);
    
                if (index === -1) {
                    return c;
                }

                return c.filter((_, i) => i !== index);
            })
            timerIds.current = timerIds.current.filter(t => t !== timer.id);

        }, 1000);

        timerIds.current.push(timer.id);

    }, [alerts]);

    const addAlert = useCallback((alert: IAlert) => {

        if (!alert.key) {
            alert.key = crypto.randomUUID();
        }

        if (alert.timeout) {
            const timer: { id?: number; } = {
                id: undefined
            };

            timer.id = window.setTimeout(() => {
                removeAlert(alert);
                timerIds.current = timerIds.current.filter(t => t !== timer.id);
            }, alert.timeout);

            timerIds.current.push(timer.id);
        }

        setAlerts(c => {
            return [
                ...c,
                alert
            ]
        });


    }, [alerts, removeAlert]);

    const context = useMemo(() => {
        return {
            alerts,
            addAlert
        }
    }, [alerts, addAlert, removeAlert]);

    return (
        <AlertContext.Provider value={context}>
            {children}
            <Snackbar style={{ marginTop: '40px' }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={alerts.length !== 0}>
                <div>
                    {
                        alerts.map((alert) => (
                            <Slide
                                key={alert.key}
                                direction='left'
                                in={!alert.dismissed}
                                mountOnEnter 
                                unmountOnExit
                            >
                                <StyledAlert
                                    icon={
                                        alert.severity === 'success' ? 
                                            <StyledCheckIcon /> : 
                                        alert.severity === 'error' ? 
                                            <StyledCloseIcon /> : 
                                        alert.severity === 'warning' ?
                                            <StyledWarningAmberIcon /> :
                                            <StyledInfoIcon />
                                    }
                                    onClose={alert.dismissable ? () => removeAlert(alert) : undefined} 
                                    severity={alert.severity ?? 'info'}
                                    styleSeverity={alert.severity ?? 'info'}
                                    sx={{mt: 2}}
                                >
                                    <AlertTitle>{alert.severity ? (alert.severity[0].toUpperCase() + alert.severity.slice(1)) : 'Info'}</AlertTitle>
                                    <Typography variant='body2'>{alert.content}</Typography>
                                </StyledAlert>
                            </Slide>
                        ))
                    }
                </div>
            </Snackbar>
        </AlertContext.Provider>
    )
}

export default AlertProvider